import React, { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'

import { CardBlockInfo, CardBody as UICardBody } from 'packages/ui/CardPreview'
import Status from 'components/global/Status'
import { parseUptime } from '../../Infras/config-columns'
import EquipmentThumbnails from '../../Infras/EquipmentThumbnails'
import { isRackType, formatBatteryTimeRemaining, availableToTranslationKey, getDeviceStatus } from '../../../helpers'
import { FIND_AP_PARENT } from '../../../operations-gql/queries'
import {
  FrequencyContainer,
  FrequencyRow,
  FrequencyLabel,
  FrequencyValue,
  SsidContainer,
  SsidTableWrapper,
  SsidTableHeader,
  SsidTableRow,
  SsidNameCell,
  SsidFreqHeader,
  SsidFreqCell,
  InfoGrid
} from './styles'
import DevicesList from './DevicesList'
import useGoToMap  from '../../../use-go-to-map'
const FrequencyInfo = ({ device }) => {
  const { t } = useTranslation()

  return (
    <FrequencyContainer>
      <FrequencyRow>
        <FrequencyLabel>
          {t('supervision.popup.frequence_24ghz')}
        </FrequencyLabel>
        <i className="fa-light fa-arrow-right" />
        <FrequencyValue>
          {`${t('supervision.card.canal')} ${device.channel_b || '-'}`}
        </FrequencyValue>
      </FrequencyRow>
      <FrequencyRow>
        <FrequencyLabel>
          {t('supervision.popup.frequence_5ghz')}
        </FrequencyLabel>
        <i className="fa-light fa-arrow-right" />
        <FrequencyValue>
          {`${t('supervision.card.canal')} ${device.channel_a || '-'}`}
        </FrequencyValue>
      </FrequencyRow>
    </FrequencyContainer>
  )
}

const FreqIcon = ({ value }) => {
  if (value === null) return null
  return <i className={`fa-light fa-${value ? 'check' : 'xmark'}`} style={{ color: value ? '#92bc62' : '#f59e0a', fontSize: 13 }} />
}

const SsidList = ({ ssids }) => {
  const { t } = useTranslation()

  if (!ssids || ssids.length === 0) return null

  return (
    <SsidContainer>
      <SsidTableWrapper>
        <SsidTableHeader>
          <SsidNameCell sx={{ fontSize: 11, fontWeight: 500, color: '#7A8096' }}>
            {t('supervision.card.ssid_name', 'SSID')}
          </SsidNameCell>
          <SsidFreqHeader>2.4 GHz</SsidFreqHeader>
          <SsidFreqHeader>5 GHz</SsidFreqHeader>
        </SsidTableHeader>
        {ssids.map((ssid) => {
          const name = typeof ssid === 'string' ? ssid : ssid.name
          const has24 = typeof ssid === 'string' ? null : ssid.has_2_4ghz
          const has5 = typeof ssid === 'string' ? null : ssid.has_5ghz
          return (
            <SsidTableRow key={name}>
              <SsidNameCell>{name}</SsidNameCell>
              <SsidFreqCell><FreqIcon value={has24} /></SsidFreqCell>
              <SsidFreqCell><FreqIcon value={has5} /></SsidFreqCell>
            </SsidTableRow>
          )
        })}
      </SsidTableWrapper>
    </SsidContainer>
  )
}

const AccessPointBody = ({ device }) => {
  const { t } = useTranslation()
  const { focusId } = useParams()
  const { go } = useGoToMap()
  const { data: parentData } = useQuery(FIND_AP_PARENT, {
    variables: { mac: device.mac },
    skip: !device.mac,
  })
  console.log(parentData)
  const parent = parentData?.ap

  const parentDeviceStatus = useMemo(() => {
    if (!parent?.parent_label) return null
    return getDeviceStatus({ available_level: parent.parent_status, is_reachable: parent.parent_is_reachable })
  }, [parent])

  return (
    <Box display={'flex'} flexDirection={'column'} mt={1} gap={2}>
      <CardBlockInfo
        title={t('supervision.popup.connected_devices')}
        subtitle={device.connected_stations || '0'}
      />
      <CardBlockInfo
        title={t('supervision.popup.frequence_canals')}
        subtitle={<FrequencyInfo device={device} />}
      />
      {parent?.parent_label && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <CardBlockInfo title={t('supervision.card.parent')}>
            <Stack onClick={() => go({ mac: parent.parent_mac, siteId: focusId })} sx={{cursor: 'pointer'}}>
              <Typography fontSize={13} fontWeight={400}>{parent.parent_label}</Typography>
              {parent.parent_mac && <Typography fontSize={11} fontWeight={400} color={'#7A8096'} fontFamily="Consolas, monospace">{parent.parent_mac}</Typography>}
            </Stack>
          </CardBlockInfo>
          {parent.parent_port && (
            <CardBlockInfo title={t('supervision.card.parent_port')} subtitle={parent.parent_port} />
          )}
          {parentDeviceStatus?.level && (
            <CardBlockInfo title={t('supervision.card.status_parent')}>
              <Box mt={0.5}>
                <Status
                  status={t(availableToTranslationKey(parentDeviceStatus.level))}
                  color={parentDeviceStatus.color}
                  bgcolor={parentDeviceStatus.bgcolor}
                  isUppercase={false}
                  fontSize={10}
                  sx={{ alignSelf: 'flex-start' }}
                />
              </Box>
            </CardBlockInfo>
          )}
        </Box>
      )}
      <SsidList ssids={device.ssids || device.ssid_names} />
    </Box>
  )
}

const FirewallBody = ({ device }) => {
  const { t } = useTranslation()

  const subtitleUptime = useMemo(() => {
    if ([0, undefined].includes(device.system_uptime)) {
      return '-'
    }
    const { nbDay, hour } = parseUptime(device.system_uptime)
    return nbDay ? t('supervision.tables.system_uptime_value', { count: nbDay, hour }) : hour
  }, [device, t])

  return (
    <>
      <CardBlockInfo
        title={t('supervision.card.total_tunnels')}
        subtitle={[undefined, null].includes(device.total_tunnels) ? '-' : device.total_tunnels}
      />
      <CardBlockInfo
        title={t('supervision.card.tunnels_up')}
        subtitle={[undefined, null].includes(device.tunnels_up) ? '-' : device.tunnels_up}
      />
      <CardBlockInfo
        title={t('supervision.tables.system_uptime_col')}
        subtitle={subtitleUptime}
      />
    </>
  )
}

const InverterBody = ({ device }) => {
  const { t } = useTranslation()

  const batteryTimeDisplay = useMemo(() => {
    if (device.battery_time_remaining == null) return '-'
    return formatBatteryTimeRemaining(device.battery_time_remaining)
  }, [device.battery_time_remaining])

  return (
    <>
      <CardBlockInfo
        title={t('supervision.card.inverter.battery_percentage')}
        subtitle={device.battery_charge != null ? `${device.battery_charge} %` : '0 %'}
      />
      <CardBlockInfo
        title={t('supervision.card.inverter.battery_remaining_autonomy')}
        subtitle={batteryTimeDisplay}
      />
      <CardBlockInfo
        title={t('supervision.card.inverter.power_consumption')}
        subtitle={device.total_load != null ? `${device.total_load} W` : '0 W'}
      />
      {device.battery_status && (
        <CardBlockInfo
          title={t('supervision.card.battery_status')}
          subtitle={device.battery_status}
        />
      )}
      {device.battery_replace_date && (
        <CardBlockInfo
          title={t('supervision.card.battery_replace_date')}
          subtitle={device.battery_replace_date}
        />
      )}
    </>
  )
}

const ParentDeviceDetails = ({ device }) => {
  const { t } = useTranslation()
  const isRack = isRackType(device.model?.type)
  const isInverter = device.model?.type === 'power-inverter'

  if (isRack) {
    return (
      <InfoGrid>
        <CardBlockInfo
          title={t('supervision.popup.manufacturer')}
          subtitle={device.model?.manufacturer || '-'}
        />
        <CardBlockInfo
          title={t('supervision.popup.model')}
          subtitle={device.model?.name || '-'}
        />
        {isInverter && <InverterBody device={device} />}
      </InfoGrid>
    )
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <InfoGrid>
        <CardBlockInfo
          title={t('supervision.popup.manufacturer')}
          subtitle={device.model?.manufacturer || '-'}
        />
        <CardBlockInfo
          title={t('supervision.popup.model')}
          subtitle={device.model?.name || '-'}
        />
        <CardBlockInfo
          title={t('supervision.popup.mac_address')}
          subtitle={device.mac || '-'}
        />
        <CardBlockInfo
          title={t('supervision.popup.ip_address')}
          subtitle={device.ip_address || '-'}
        />
        {isInverter && <InverterBody device={device} />}
      </InfoGrid>

      {device.model?.type === 'ap' && <AccessPointBody device={device} />}
      {device.model?.type === 'firewall' && <FirewallBody device={device} />}
    </Box>
  )
}

const CardBody = ({ device, activeTab }) => {
  const { t } = useTranslation()
  const isParent = device.children && device.children.length > 0
  const isInverter = device.model?.type === 'power-inverter'

  if (isParent) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {activeTab === 'informations' ? (
          <>
            <ParentDeviceDetails device={device} />
            <EquipmentThumbnails uuid={device.uuid} />
          </>
        ) : (
          <DevicesList children={device.children} />
        )}
      </Box>
    )
  }

  return (
    <>
      <UICardBody grid>
        <CardBlockInfo
          title={t('supervision.popup.manufacturer')}
          subtitle={device.model?.manufacturer || '-'}
        />
        <CardBlockInfo
          title={t('supervision.popup.model')}
          subtitle={device.model?.name || '-'}
        />
        <CardBlockInfo
          title={t('supervision.popup.mac_address')}
          subtitle={device.mac || '-'}
        />
        <CardBlockInfo
          title={t('supervision.popup.ip_address')}
          subtitle={device.ip_address || '-'}
        />
        {device.model?.type === 'firewall' && <FirewallBody device={device} />}
        {isInverter && <InverterBody device={device} />}
      </UICardBody>
      {device.model?.type === 'ap' && <AccessPointBody device={device} />}
      <EquipmentThumbnails uuid={device.uuid} />
    </>
  )
}

export default CardBody